import Checklist, { BusinessInfoSupportingItemInput } from 'jobs/Checklist'
import InfoGatheringStore from 'jobs/InfoGatheringStore'
import BusinessProfileStore from './BusinessProfileStore'
import Job from 'jobs/Job'
import { computed, makeObservable } from 'mobx'
import { toStream } from 'utils/mobx-util'
import { switchMap } from 'rxjs/operators'
import { whileActive, withCancellation } from 'utils/rx-util'
import { CancellationToken } from 'infra/cancellation'
import { task } from 'mobx-task'
import { BehaviorSubject } from 'rxjs'

export default class BusinessInfoViewModel {
  // Tracks whether the view model is active.
  private readonly _active = new BehaviorSubject<boolean>(false)

  constructor(
    public job: Job,
    public infoGatheringStore: InfoGatheringStore,
    public businessProfileStore: BusinessProfileStore,
    private getChecklist: () => Checklist | undefined,
  ) {
    makeObservable(this)

    // reacts to changes in the supporting item to re-fetch the profile.
    toStream(() => {
      return {
        profileVersion: this.businessSupportingItemInput?.profileVersion,
        profileId: this.businessSupportingItemInput?.profileId,
      }
    }, true)
      .pipe(
        whileActive(this._active),
        switchMap(() => withCancellation(this._onRemoteItemUpdated)),
      )
      .subscribe()
  }

  /**
   * The business profile version.
   */
  @computed
  get profile() {
    if (!this.businessSupportingItemInput?.profileVersion) {
      return null
    }

    return this.businessProfileStore.getBusinessProfile(
      this.businessSupportingItemInput?.profileId,
      this.businessSupportingItemInput?.profileVersion,
    )
  }

  /**
   * The checklist.
   */
  @computed
  get checklist() {
    return this.getChecklist()
  }

  /**
   * The business section.
   */
  @computed
  get businessSection() {
    return this.checklist?.businessInfoSection
  }

  /**
   * The supporting item.
   */
  @computed
  get businessSupportingItem() {
    return this.businessSection?.businessInfoSupportingItem
  }

  /**
   * The supporting item input.
   */
  @computed
  get businessSupportingItemInput() {
    return this.businessSupportingItem?.input as BusinessInfoSupportingItemInput
  }

  /**
   * Activates the vm.
   */
  activate = task.resolved(async () => {
    this._active.next(true)

    if (this.businessSupportingItem?.completed) {
      await this.businessProfileStore.fetchBusinessProfileForJob(this.job.id)
    }
  })

  /**
   * Deactivates the vm.
   */
  deactivate() {
    this._active.next(false)
  }

  /**
   * Updates the business profile when remotes update happens.
   */
  private _onRemoteItemUpdated = task(async (ct: CancellationToken) => {
    if (this.businessSupportingItem?.completed) {
      await ct.race(
        this.businessProfileStore.fetchBusinessProfileForJob(this.job.id),
      )
    }
  })
}
